/**
 * Splits a non-negative amount of cents across weights by largest remainder
 * (ties to the lowest index), so the parts always sum to the total.
 */
export function allocateCents(totalCents: number, weights: number[]): number[] {
  const parts = weights.map(() => 0);
  const total = Math.max(0, Math.round(totalCents));
  if (weights.length === 0 || total === 0) return parts;

  const clamped = weights.map((weight) => (Number.isFinite(weight) && weight > 0 ? weight : 0));
  const weightSum = clamped.reduce((acc, weight) => acc + weight, 0);
  const effective = weightSum > 0 ? clamped : clamped.map(() => 1);
  const effectiveSum = weightSum > 0 ? weightSum : clamped.length;

  const exact = effective.map((weight) => (total * weight) / effectiveSum);
  exact.forEach((value, index) => {
    parts[index] = Math.floor(value);
  });

  let left = total - parts.reduce((acc, part) => acc + part, 0);
  const byRemainder = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const { index } of byRemainder) {
    if (left <= 0) break;
    parts[index] += 1;
    left -= 1;
  }
  return parts;
}
